// Bilingual strings for the public OCIA registration form.
// Enum option arrays ({value,label}) are embedded per-language so selects render
// translated labels while POSTing canonical English values.

const en = {
  locale: 'en-US',
  languageLabel: { en: 'English', es: 'Español' },

  header: 'OCIA Registration',
  intro: 'Order of Christian Initiation of Adults — St. Bridget University Parish',

  closedTitle: 'Registration Closed',
  closedBody: 'OCIA registration is not currently open. Please contact the parish office for assistance.',

  successTitle: 'Registration Received',
  successBody: 'Thank you for registering for OCIA at St. Bridget University Parish. Someone from our team will be in touch.',
  successCashReminder: 'You selected to pay in person. Please submit your payment at the parish office.',

  steps: ['Personal', 'Contact', 'Religious History', 'Marital Status', 'Faith Journey', 'Signature', 'Payment'],
  next: 'Next',
  back: 'Back',
  submit: 'Submit Registration',
  submitting: 'Submitting…',
  requiredNotice: 'Please complete the required fields before continuing.',

  // Step 0 — Personal
  personalTitle: 'Personal Information',
  nameSection: 'Name',
  parentsSection: 'Parents',
  fillTestData: 'Fill Test Data',
  firstName: 'First Name',
  middleName: 'Middle Name',
  lastName: 'Last Name',
  maidenName: 'Maiden Name (if applicable)',
  dateOfBirth: 'Date of Birth',
  placeOfBirth: 'Place of Birth',
  city: 'City',
  stateProvince: 'State/Province',
  postalCode: 'Postal Code',
  country: 'Country',
  fatherName: "Father's Name",
  motherName: "Mother's Name",
  motherMaidenName: "Mother's Maiden Name",

  // Step 1 — Contact
  contactTitle: 'Contact Information',
  areYouStudent: 'Are you a student?',
  liveOnCampus: 'Do you live on campus?',
  yes: 'Yes',
  no: 'No',
  homeAddress: 'Home Address',
  schoolAddress: 'School Address',
  street: 'Street',
  state: 'State',
  zip: 'Zip',
  cellPhone: 'Cell Phone',
  occupation: 'Occupation',
  email: 'Email',

  // Step 2 — Religious history
  religiousHistoryTitle: 'Religious History',
  presentAffiliation: 'What, if any, is your present religious affiliation?',
  everBaptized: 'Have you ever been baptized?',
  baptizedOptions: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unsure', label: 'Unsure' }
  ],
  baptismDetails: 'Baptism Details',
  denomination: 'In what denomination?',
  dateOrAge: 'Date or approximate age when baptized',
  baptismalName: 'Baptismal name (if different from current name)',
  churchName: 'Place of Baptism (name of church)',
  churchAddress: 'Address (if known)',
  location: 'Location (if known)',
  catholicSacramentsPrompt: 'If you were baptized Catholic, check the sacraments you have already received:',
  sacramentOptions: [
    { value: 'penance', label: 'Penance (Confession)' },
    { value: 'eucharist', label: 'Eucharist (First Communion)' },
    { value: 'confirmation', label: 'Confirmation' }
  ],

  // Step 3 — Marital status
  maritalTitle: 'Marital Status',
  maritalStatusLabel: 'Marital Status',
  maritalStatusOptions: [
    { value: 'never-married', label: 'Never Married' },
    { value: 'engaged', label: 'Engaged to be Married' },
    { value: 'married', label: 'Married' },
    { value: 'separated', label: 'Married, but Separated' },
    { value: 'divorced', label: 'Divorced, Not Remarried' },
    { value: 'widowed', label: 'Widow/Widower, Not Remarried' }
  ],
  fiance: 'Fiancé(e)',
  spouse: 'Spouse',
  partnerName: 'Name',
  partnerAffiliation: 'Current Religious Affiliation',
  partnerMarriedBeforeFiance: 'Has your fiancé(e) been previously married?',
  partnerMarriedBeforeSpouse: 'Has your spouse been previously married?',
  marriageDetails: 'Marriage Details',
  dateOfMarriage: 'Date of Marriage',
  placeOfMarriage: 'Place of Marriage',
  officiatingAuthority: 'Officiating Authority',
  officiatingOptions: [
    { value: 'civil', label: 'Civil Government' },
    { value: 'non-christian-minister', label: 'Non-Christian Minister' },
    { value: 'christian-minister', label: 'Christian Minister' },
    { value: 'catholic-cleric', label: 'Catholic Cleric' }
  ],
  widowedStillLiving: 'Were you previously married to someone who is still living?',
  registrantMarriedBefore: 'Have you been previously married?',
  previousMarriage: 'Previous Marriage',
  addPreviousMarriage: 'Add Previous Marriage',
  remove: 'Remove',

  // Step 4 — Faith journey
  faithJourneyTitle: 'Faith Journey',
  q1: 'What or who has led you to want to know more about the Catholic faith?',
  q2: 'Please describe the types of religious education you have received, as a child and as an adult.',
  q3: 'What contact have you had with the Catholic Church to date?',
  q4: 'What are some of the questions or concerns you have about the Catholic Church?',
  intentPrompt: 'At this point in time, which of the following statements best describes your present feelings about the possibility of joining the Catholic Church? (Please select one)',
  intentConsidering: 'I am considering joining, but I am still unsure about it. I need more information about the Catholic Church before I would consider joining.',
  intentFairlySure: (date) => `I am fairly sure that I would like to join, but I still need some time to study and pray about it. I will attend the classes seeking more knowledge of the faith and make a decision by ${date}.`,
  intentSure: 'I am sure that I want to join the Catholic Church. I will be attending the classes.',
  toBeDetermined: 'a date to be determined',

  // Step 5 — Signature
  signatureTitle: 'Signature',
  signatureName: 'Full Name (signature)',
  signatureDate: 'Date',

  // Step 6 — Payment
  paymentTitle: 'Payment',
  registrationFee: 'Registration Fee',
  discountApplied: 'Early Discount Applied',
  amountDue: 'Amount Due',
  noFee: 'There is no fee required to submit this registration.',
  howToPay: 'How would you like to pay?',
  payOnline: 'Pay Online',
  payInPerson: 'Pay in Person',
  cardInfo: 'Card Information',
  loadingPaymentForm: 'Loading payment form…',
  paymentSystemFailed: 'The payment system failed to load. You may still submit and pay in person.',
  cardDetailsInvalid: 'Card details are invalid.',
  paymentFailed: 'Payment failed. Please try again or choose Pay in Person.',

  validationFix: 'Please complete all required fields.',
  genericError: 'Something went wrong. Please try again.'
};

const es = {
  locale: 'es-US',
  languageLabel: { en: 'English', es: 'Español' },

  header: 'Registro de OCIA',
  intro: 'Orden de la Iniciación Cristiana de Adultos — Parroquia Universitaria St. Bridget',

  closedTitle: 'Registro Cerrado',
  closedBody: 'El registro de OCIA no está abierto actualmente. Comuníquese con la oficina parroquial.',

  successTitle: 'Registro Recibido',
  successBody: 'Gracias por registrarse para OCIA en la Parroquia Universitaria St. Bridget. Alguien de nuestro equipo se comunicará con usted.',
  successCashReminder: 'Eligió pagar en persona. Por favor entregue su pago en la oficina parroquial.',

  steps: ['Personal', 'Contacto', 'Historia Religiosa', 'Estado Civil', 'Camino de Fe', 'Firma', 'Pago'],
  next: 'Siguiente',
  back: 'Atrás',
  submit: 'Enviar Registro',
  submitting: 'Enviando…',
  requiredNotice: 'Complete los campos obligatorios antes de continuar.',

  personalTitle: 'Información Personal',
  nameSection: 'Nombre',
  parentsSection: 'Padres',
  fillTestData: 'Llenar Datos de Prueba',
  firstName: 'Nombre',
  middleName: 'Segundo Nombre',
  lastName: 'Apellido',
  maidenName: 'Apellido de Soltera (si aplica)',
  dateOfBirth: 'Fecha de Nacimiento',
  placeOfBirth: 'Lugar de Nacimiento',
  city: 'Ciudad',
  stateProvince: 'Estado/Provincia',
  postalCode: 'Código Postal',
  country: 'País',
  fatherName: 'Nombre del Padre',
  motherName: 'Nombre de la Madre',
  motherMaidenName: 'Apellido de Soltera de la Madre',

  contactTitle: 'Información de Contacto',
  areYouStudent: '¿Es usted estudiante?',
  liveOnCampus: '¿Vive usted en el campus?',
  yes: 'Sí',
  no: 'No',
  homeAddress: 'Dirección de Casa',
  schoolAddress: 'Dirección de la Escuela',
  street: 'Calle',
  state: 'Estado',
  zip: 'Código Postal',
  cellPhone: 'Teléfono Celular',
  occupation: 'Ocupación',
  email: 'Correo Electrónico',

  religiousHistoryTitle: 'Historia Religiosa',
  presentAffiliation: '¿Cuál es su afiliación religiosa actual, si tiene alguna?',
  everBaptized: '¿Alguna vez ha sido bautizado/a?',
  baptizedOptions: [
    { value: 'yes', label: 'Sí' },
    { value: 'no', label: 'No' },
    { value: 'unsure', label: 'No estoy seguro/a' }
  ],
  baptismDetails: 'Detalles del Bautismo',
  denomination: '¿En qué denominación?',
  dateOrAge: 'Fecha o edad aproximada del bautismo',
  baptismalName: 'Nombre de bautismo (si es diferente al actual)',
  churchName: 'Lugar del Bautismo (nombre de la iglesia)',
  churchAddress: 'Dirección (si la conoce)',
  location: 'Ubicación (si la conoce)',
  catholicSacramentsPrompt: 'Si fue bautizado/a católico/a, marque los sacramentos que ya ha recibido:',
  sacramentOptions: [
    { value: 'penance', label: 'Penitencia (Confesión)' },
    { value: 'eucharist', label: 'Eucaristía (Primera Comunión)' },
    { value: 'confirmation', label: 'Confirmación' }
  ],

  maritalTitle: 'Estado Civil',
  maritalStatusLabel: 'Estado Civil',
  maritalStatusOptions: [
    { value: 'never-married', label: 'Nunca Casado/a' },
    { value: 'engaged', label: 'Comprometido/a para Casarse' },
    { value: 'married', label: 'Casado/a' },
    { value: 'separated', label: 'Casado/a, pero Separado/a' },
    { value: 'divorced', label: 'Divorciado/a, No Vuelto a Casar' },
    { value: 'widowed', label: 'Viudo/a, No Vuelto a Casar' }
  ],
  fiance: 'Prometido/a',
  spouse: 'Cónyuge',
  partnerName: 'Nombre',
  partnerAffiliation: 'Afiliación Religiosa Actual',
  partnerMarriedBeforeFiance: '¿Su prometido/a ha estado casado/a anteriormente?',
  partnerMarriedBeforeSpouse: '¿Su cónyuge ha estado casado/a anteriormente?',
  marriageDetails: 'Detalles del Matrimonio',
  dateOfMarriage: 'Fecha del Matrimonio',
  placeOfMarriage: 'Lugar del Matrimonio',
  officiatingAuthority: 'Autoridad que Ofició',
  officiatingOptions: [
    { value: 'civil', label: 'Gobierno Civil' },
    { value: 'non-christian-minister', label: 'Ministro No Cristiano' },
    { value: 'christian-minister', label: 'Ministro Cristiano' },
    { value: 'catholic-cleric', label: 'Clérigo Católico' }
  ],
  widowedStillLiving: '¿Estuvo casado/a anteriormente con alguien que aún vive?',
  registrantMarriedBefore: '¿Ha estado casado/a anteriormente?',
  previousMarriage: 'Matrimonio Anterior',
  addPreviousMarriage: 'Agregar Matrimonio Anterior',
  remove: 'Eliminar',

  faithJourneyTitle: 'Camino de Fe',
  q1: '¿Qué o quién lo ha llevado a querer conocer más sobre la fe católica?',
  q2: 'Describa los tipos de educación religiosa que ha recibido, de niño/a y de adulto/a.',
  q3: '¿Qué contacto ha tenido con la Iglesia Católica hasta ahora?',
  q4: '¿Cuáles son algunas de las preguntas o inquietudes que tiene sobre la Iglesia Católica?',
  intentPrompt: 'En este momento, ¿cuál de las siguientes afirmaciones describe mejor sus sentimientos actuales sobre la posibilidad de unirse a la Iglesia Católica? (Seleccione una)',
  intentConsidering: 'Estoy considerando unirme, pero todavía no estoy seguro/a. Necesito más información sobre la Iglesia Católica antes de considerarlo.',
  intentFairlySure: (date) => `Estoy bastante seguro/a de que me gustaría unirme, pero aún necesito tiempo para estudiar y orar al respecto. Asistiré a las clases buscando más conocimiento de la fe y tomaré una decisión antes del ${date}.`,
  intentSure: 'Estoy seguro/a de que quiero unirme a la Iglesia Católica. Asistiré a las clases.',
  toBeDetermined: 'una fecha por determinar',

  signatureTitle: 'Firma',
  signatureName: 'Nombre Completo (firma)',
  signatureDate: 'Fecha',

  paymentTitle: 'Pago',
  registrationFee: 'Cuota de Registro',
  discountApplied: 'Descuento Anticipado Aplicado',
  amountDue: 'Monto a Pagar',
  noFee: 'No se requiere ninguna cuota para enviar este registro.',
  howToPay: '¿Cómo desea pagar?',
  payOnline: 'Pagar en Línea',
  payInPerson: 'Pagar en Persona',
  cardInfo: 'Información de la Tarjeta',
  loadingPaymentForm: 'Cargando formulario de pago…',
  paymentSystemFailed: 'El sistema de pago no se pudo cargar. Aún puede enviar y pagar en persona.',
  cardDetailsInvalid: 'Los datos de la tarjeta no son válidos.',
  paymentFailed: 'El pago falló. Intente de nuevo o elija Pagar en Persona.',

  validationFix: 'Complete todos los campos obligatorios.',
  genericError: 'Algo salió mal. Por favor intente de nuevo.'
};

export const translations = { en, es };
